<i18n lang="yaml">
en:
  members: Members
  describing_text: 'Here you can add or remove members from your organization'
  add: Add
  placeholder_email: 'Email'
  placeholder_username: 'UNIX username'
  invitation_sent: 'Invitation sent'
  member_deleted: 'Member deleted'
ru:
  members: Участники
  describing_text: 'Здесь можно добавлять или удалять участников вашей организации'
  add: Добавить
  placeholder_email: 'Email'
  placeholder_username: 'UNIX имя пользователя'
  invitation_sent: 'Приглашение отправлено'
  member_deleted: 'Участник удален'
</i18n>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useMyFetch } from '@/utils'
import { useRoute } from 'vue-router'
import { useAlertStore, useAuthStore } from '@/store'
import MemberCard from '@/components/MemberCard.vue'
import RoleSelector from '@/components/RoleSelector.vue'
import { Field, Form, type FormState } from 'vee-validate'
import * as yup from 'yup'
import { onMounted, ref } from 'vue'
import { handleError } from '@/utils/errors'
import router from '@/router'

interface Member {
  membership_id: string
  email: string
  role: string
  tags: Array<string>
  is_active: boolean
  unix_username: string
}

const { t } = useI18n()
const route = useRoute()
const alertStore = useAlertStore()
const authStore = useAuthStore()
const members = ref<Array<Member>>([])
const email = ref()
const username = ref()
const role = ref('member')

const fetchMembers = async () => {
  const { data, error } = await useMyFetch(
    `organizations/${route.params.organization_id}/members`
  ).json()
  if (error.value) {
    await handleError(error, router, authStore, alertStore, 'organizations:list')
  } else {
    members.value = data.value
  }
}

onMounted(async () => {
  await fetchMembers()
})

function isBlank(str: string) {
  return !str || /^\s*$/.test(str)
}

const schema_email: yup.StringSchema = yup.string().email().required()
const schema_username: yup.StringSchema = yup
  .string()
  .required()
  .matches(/^[a-z_]([a-z0-9_-]{0,31}|[a-z0-9_-]{0,30}\$)$/)
const schema = yup.object({
  email: schema_email,
  username: schema_username
})

async function onEmailChange() {
  if (isBlank(email.value)) {
    username.value = ''
  }
  schema_email.isValid(email.value).then((valid) => {
    if (valid) {
      email.value = email.value.toLowerCase()
      if (isBlank(username.value)) {
        const candidateUsername = email.value.split('@')[0].replace(/[^a-z0-9_-]/g, '')
        schema_username.validate(candidateUsername).then((valid) => {
          if (valid) {
            username.value = candidateUsername
          }
        })
      }
    }
  })
}

async function deleteMember(member_id: String) {
  const { error } = await useMyFetch(
    `organizations/${route.params.organization_id}/members/${member_id}`
  ).delete()
  if (error.value) {
    await handleError(error, router, authStore, alertStore)
  } else {
    alertStore.success(t('member_deleted'))
    await fetchMembers()
  }
}

type ResetFormCallback = (state?: Partial<FormState<any>>) => void

async function onInvite(values: any, { resetForm }: { resetForm: ResetFormCallback }) {
  const { error } = await useMyFetch(`organizations/${route.params.organization_id}/invite`)
    .post({
      email: values.email,
      username: values.username,
      role: role.value
    })
    .json()
  if (error.value) {
    await handleError(error, router, authStore, alertStore)
  } else {
    resetForm()
    alertStore.success(t('invitation_sent'))
    await fetchMembers()
  }
}
</script>

<template>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <h4 class="card-header">{{ t('members') }}</h4>
        <div class="card-body">
          <p class="card-text">{{ t('describing_text') }}</p>
          <Form
            novalidate
            @submit="onInvite"
            :validation-schema="schema"
            v-slot="{ errors, isSubmitting }"
          >
            <div class="input-group mb-3">
              <Field
                v-model="email"
                name="email"
                type="email"
                @change="onEmailChange"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                v-bind:placeholder="t('placeholder_email')"
                :validateOnBlur="false"
              />
              <Field
                v-model="username"
                name="username"
                type="text"
                class="form-control ms-2"
                :class="{ 'is-invalid': errors.username }"
                v-bind:placeholder="t('placeholder_username')"
                :validateOnBlur="false"
              />
              <RoleSelector class="mx-2" v-model="role" />
              <button class="btn btn-outline-primary" :disabled="isSubmitting">
                <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
                <img src="@/assets/plus.svg" />{{ t('add') }}
              </button>
            </div>
          </Form>

          <div class="row row-cols-1 row-cols-md-2 row-cols-xl-2 mt-2">
            <div class="col mt-2" v-for="member in members" :key="member.membership_id">
              <MemberCard
                v-on:delete="deleteMember"
                :id="member.membership_id"
                :email="member.email"
                :role="member.role"
                :is_invited="!member.is_active"
                :tags="member.tags"
                :unix_username="member.unix_username"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
